import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import type { AppConfig } from '#/config/types/AppConfig';
import { HORIZON_ENABLED } from '#/rendering/state/renderingState';
import type { FilestackImageFit } from '#/types/Filestack';

type Args = {
  data: ApiFilestackImageData;
  width: number | undefined;
  height: number | undefined;
  fit: FilestackImageFit;
  appConfig: AppConfig;
  /**
   * TODO: invert `insecureSVG` to `secureSVG` to make sure we're always taking
   * this property into account
   */
  insecureSVG: boolean;
  quality?: number;
};

export function imageUrl({
  data,
  width,
  height,
  fit,
  insecureSVG,
  quality,
  appConfig,
}: Args) {
  const { handle, mimetype } = data;

  if (appConfig.filestack.mock === 'storybook') {
    return `/storybook-fake/${handle}`;
  }

  const baseUrl = (() => {
    if (appConfig.filestack.mock) return '/lolz/image';
    if (HORIZON_ENABLED || appConfig.filestack.proxy) return `/images`;

    return 'https://cdn.filestackcontent.com';
  })();

  /*
   * Converting SVGs to WebPs causes them to lose too much quality
   */
  if (mimetype?.includes('image/svg')) {
    const secure = insecureSVG ? 'false' : 'true';
    return `${baseUrl}/no_metadata/output=secure:${secure}/compress/cache=expiry:max/${handle}`;
  }

  const resize = [
    width && `w:${Math.round(width)}`,
    height && `h:${Math.round(height)}`,
  ]
    .filter(Boolean)
    .join(',');

  const defaultFit = width && height ? 'crop' : 'max';
  const resizeQuery = resize && `resize=${resize},fit:${fit || defaultFit}`;
  const qualityQuery = `quality=value:${quality ?? 70}`;
  const outputFormat = mimetype?.includes('image/gif')
    ? ''
    : 'output=format:webp';

  // The order in which these items are put together matters a lot to Filestack
  //
  // Transformations are processed in order and if we order these transformations
  // incorrectly we can end up with much larger files
  return [
    baseUrl,
    'no_metadata',
    outputFormat,
    resizeQuery,
    qualityQuery,
    'compress',
    'cache=expiry:max',
    handle,
  ]
    .filter(Boolean)
    .join('/');
}

/**
 * Wrapper for imageUrl with settings for og:image metadata
 */
export function imageUrlForOgMeta<
  T extends ApiFilestackImageData | null | undefined,
>({
  data,
  appConfig,
}: {
  data: T;
  appConfig: AppConfig;
}): T extends ApiFilestackImageData ? string : null {
  // @ts-expect-error TS is not smart enough to catch this if/else
  if (!data) return null;

  const url = imageUrl({
    data,
    width: 600,
    height: 315,
    fit: 'max',
    insecureSVG: false,
    quality: 90, // historically we set quality to 90 for ogImages
    appConfig,
  });

  const fullUrl = `${appConfig.idealist.appUrl}${url}`;

  // @ts-expect-error TS is not smart enough to catch this if/else
  return fullUrl;
}
